import { memo, useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { Popover, PopoverContent, PopoverTrigger } from 'root/src/client/v2/common/components/ui/popover';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignShareMenu } from 'root/src/client/v2/common/components/RedesignShareMenu';
import { RedesignMenuItem } from 'root/src/client/v2/common/components/RedesignMenuItem';
import { RedesignDeliverDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignDeliverDareModal';
import { RedesignRejectDareModal } from 'root/src/client/v2/_pages/drop/redesign/RedesignRejectDareModal';
import { isClipsOnlyDare, isDareInFlight } from 'root/src/client/v2/_pages/drop/redesign/dareCta';
import { cn } from 'root/src/client/v2/common/helpers/cn';
// Matches the RedesignShareMenu trigger so the two buttons read as one pair.
const TRIGGER_BASE = 'flex h-8 w-8 items-center justify-center rounded-lg transition-colors';
const TRIGGER_VARIANT = {
    // The glass chrome RedesignShareMenu ships with by default, so the kebab matches it over artwork.
    overlay: 'border border-solid border-white/[0.08] bg-background/60 text-foreground backdrop-blur-md hover:bg-background/80',
    // No chrome until hover, as on the creator profile card, where the buttons sit on the card surface.
    inline: 'border-transparent bg-transparent text-muted-foreground backdrop-blur-none hover:bg-white/[0.06] hover:text-foreground',
};
// The dare's own public page (/dare/:projectUrl, RedesignViewDarePage). Built from the slug rather
// than read off the payload: only project records carry a stored `shareUrl`, and the drop response is
// shared with the legacy drop page, whose schema rejects unknown fields, so adding one server-side
// would break that page. The dares section is client-rendered only (the drop is fetched through
// useDropEvent), so reading the origin during render cannot mismatch server HTML.
const dareShareUrl = (dare) => (dare.projectUrl && typeof window !== 'undefined'
    ? `${window.location.origin}/dare/${dare.projectUrl}`
    : undefined);
export const DareCardActions = memo(({ dare, refetchDropEvent, variant = 'overlay', onOpenChange, className, }) => {
    const [open, setOpen] = useState(false);
    const redesignModal = useRedesignModal();
    const shareUrl = dareShareUrl(dare);
    // Deliver and Reject both act on the viewer's own project, so the kebab only exists once they have
    // accepted this dare and it has not run to a terminal state. Share needs no such gate, but a dare
    // nobody accepted has no page to link to either, so in practice both appear together.
    const canManage = isDareInFlight(dare);
    const triggerClass = cn(TRIGGER_BASE, TRIGGER_VARIANT[variant]);
    const setMenuOpen = (next) => {
        setOpen(next);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(next);
    };
    // Same modal and props as the Deliver CTA (useDareCta), so delivering from the menu and from the
    // button are the same flow, clips-only rule included.
    const onDeliver = () => {
        var _a;
        setMenuOpen(false);
        redesignModal.show(<RedesignDeliverDareModal projectUrl={(_a = dare.projectUrl) !== null && _a !== void 0 ? _a : ''} platforms={dare.platforms} clipsOnly={isClipsOnlyDare(dare)} onSuccess={refetchDropEvent}/>);
    };
    const onReject = () => {
        var _a;
        setMenuOpen(false);
        redesignModal.show(<RedesignRejectDareModal projectUrl={(_a = dare.projectUrl) !== null && _a !== void 0 ? _a : ''} onSuccess={refetchDropEvent}/>);
    };
    if (!shareUrl && !canManage) {
        return null;
    }
    return (<div className={cn('flex shrink-0 items-center gap-1', className)}>
			{shareUrl && (<RedesignShareMenu url={shareUrl} onOpenChange={onOpenChange} className={variant === 'inline' ? triggerClass : undefined}/>)}
			{canManage && (<Popover open={open} onOpenChange={setMenuOpen}>
					<PopoverTrigger asChild>
						<button type="button" aria-label="Dare options" className={triggerClass}>
							<MoreVertIcon sx={{ fontSize: 18 }}/>
						</button>
					</PopoverTrigger>
					<PopoverContent align="end" className="w-44 p-1">
						<RedesignMenuItem icon={<SendOutlinedIcon sx={{ fontSize: 16 }}/>} label="Deliver" onClick={onDeliver}/>
						<RedesignMenuItem icon={<CancelOutlinedIcon sx={{ fontSize: 16 }}/>} label="Reject" onClick={onReject} destructive/>
					</PopoverContent>
				</Popover>)}
		</div>);
});
DareCardActions.displayName = 'DareCardActions';
